// LSP push-diagnostics → CodeMirror lint adapter.
//
// `@codemirror/lint` is built around a pull model: the editor calls a
// `LintSource` after edits / idle. The LSP is push: the server emits
// `publishDiagnostics` whenever its analysis settles. We bridge the two
// by caching the latest LSP diagnostics inside a ViewPlugin and feeding
// them to a stub `LintSource`, then calling `forceLinting` whenever the
// cache changes so the gutter refreshes within one frame instead of
// waiting for the next user-typing tick.
//
// `lintGutter()` adds the colour-coded margin marker; we expose it
// here so callers don't have to remember to add it alongside this
// extension. The lint panel and tooltip come from the same package and
// only need the `linter()` extension to be present, so no extra wiring
// here.

import { type Diagnostic, forceLinting, linter, lintGutter } from '@codemirror/lint';
import type { Extension } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';
import { ViewPlugin } from '@codemirror/view';
import type { LspClient } from '../client.ts';
import { lspToCmDiagnostic } from '../diagnostics.ts';

interface DiagnosticsCache {
    cached: readonly Diagnostic[];
}

export function lspLint(client: LspClient): Extension {
    const lintPlugin = ViewPlugin.fromClass(
        class implements DiagnosticsCache {
            cached: readonly Diagnostic[] = [];
            private readonly sub: { dispose(): void };

            constructor(view: EditorView) {
                this.sub = client.onDiagnostics((params) => {
                    if (params.uri !== client.uri) return;
                    this.cached = params.diagnostics.map((d) =>
                        lspToCmDiagnostic(d, view.state.doc),
                    );
                    // forceLinting bypasses the default 750ms debounce
                    // so the gutter reflects the worker's verdict the
                    // moment it arrives.
                    forceLinting(view);
                });
            }

            destroy(): void {
                this.sub.dispose();
            }
        },
    );

    const source = (view: EditorView): readonly Diagnostic[] => {
        const plugin = view.plugin(lintPlugin);
        return plugin?.cached ?? [];
    };

    return [
        lintPlugin,
        // delay: 0 keeps the linter from holding the cache hostage for
        // its default debounce window — the LSP already debounces on
        // the server side; double-debouncing here just delays the
        // squiggly without saving any work.
        linter(source, { delay: 0 }),
        lintGutter(),
    ];
}
